import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import shortid from 'shortid'

import { error } from '@react/react-spectrum/Toast'
import { useProject } from 'hooks'
import { EDITOR, PATHS } from '../config/constants'
import { immutableMerge } from 'utils/collections'

const roundToGrid = (v) => (v > 0 ? Math.floor((v + EDITOR.GRID_SIZE / 2) / EDITOR.GRID_SIZE) * EDITOR.GRID_SIZE : 0)
const positionOnGrid = (left, top) => ({ left: roundToGrid(left), top: roundToGrid(top) })

export const useScreens = () => {
  const history = useHistory()
  const { project, config, updateProjectPart, updateProjectScreen, renameProjectScreen } = useProject()

  const screens = useMemo(
    () => (Array.isArray(project?.screens) ? project.screens : Object.values(project?.screens || {})),
    [project.screens]
  )

  const setScreens = (_screens, immediate = false) => {
    const updatedScreens = Array.isArray(_screens)
      ? _screens.reduce((acc, s) => ({ ...acc, [s.meta.name]: s }), {})
      : _screens
    updateProjectPart('screens', updatedScreens, immediate)
  }

  const setScreen = (_name, _screen) => setScreens(screens.map((s) => (s.meta.name === _name ? _screen : s)))

  const setScreenWithoutEditor = (_name, _screen) => {
    setScreens(screens.map((s) => (s.meta.name === _name ? { ..._screen, editor: s.editor } : s)))
  }

  const updateScreen = (screenName, partialScreen) => {
    const screen = screens.find((s) => s.meta.name === screenName)
    updateProjectScreen(screenName, immutableMerge(screen, partialScreen))
  }

  const setNewPosition = (name, left = 0, top = 0) => {
    // Check if there is another screen on destination coordinates. If yes - try nearest place in down-right direction.
    const newPosition = positionOnGrid(left, top)
    if (
      screens.find(
        (s) =>
          s.meta.name !== name &&
          (s.editor?.position?.left || 0) === newPosition.left &&
          (s.editor?.position?.top || 0) === newPosition.top
      )
    ) {
      return setNewPosition(name, newPosition.left + EDITOR.GRID_SIZE, newPosition.top + EDITOR.GRID_SIZE)
    }

    return newPosition
  }

  const addScreen = (template) => {
    if (!template) {
      error('Cannot create screen. Template properties are missing')
      return
    }

    const meta = template.meta || {}

    const screenName = template.name || shortid()
    const position = setNewPosition(
      screenName,
      template.editor?.position?.left || 0,
      template.editor?.position?.top || 0
    )

    const isFirstScreen = screens.length === 0
    const newScreen = {
      meta: {
        name: screenName,
        title: template.title || meta.title || screenName,
        includeInNavigation: isFirstScreen,
        isRootScreen: isFirstScreen,
        navigationOrder: isFirstScreen ? 1 : null,
        template: template.template || meta.template,
        preset: template.preset || meta.preset,
        hasParent: template.hasParent || meta.hasParent,
        hidden: template.hidden || meta.hidden,
        product: template.product,
        event: template.event,
      },
      content: generateComponentsIds(template.content) || {},
      editor: { position, zIndex: screens.length },
    }

    updateProjectScreen(screenName, newScreen)
  }

  const addProductScreen = (productId) => {
    const template = {
      ...config.freeformTemplates.find((t) => t.preset === 'product'),
      product: productId,
      event: 'productView',
      title: project?.products?.find((p) => p.id === productId)?.name,
    }
    return addScreen(template)
  }

  const moveScreen = (name, left, top) => {
    // Update posision and reorder z-index of screens using algorithm:
    // 1. Set calculated position and extremely high z-index of modified screen
    // 2. Sort screens by z-index to find new order
    // 3. Set new z-index of each screen matching its order in sortedScreen array
    const updatedScreens = screens
      .map((s) =>
        s.meta.name === name
          ? { ...s, editor: { ...s.editor, position: setNewPosition(name, left, top), zIndex: 10000 } }
          : s
      )
      .sort((a, b) => (a.editor?.zIndex || 0) - (b.editor?.zIndex || 0))
      .map((s, i) => ({ ...s, editor: { ...s.editor, zIndex: i } }))

    setScreens(updatedScreens)
  }

  const updateScreenProperties = (screenName, meta) => {
    updateScreen(screenName, { meta })
  }

  const updateScreenProperty = (screenName, fieldName, value) => {
    updateScreenProperties(screenName, { [fieldName]: value })
  }

  const setScreenContent = (screenName, content) => {
    const screen = screens.find((s) => s.meta.name === screenName)
    updateProjectScreen(screenName, { ...screen, content })
  }

  const deleteScreen = (screenName) => {
    if (screens.length > 1) {
      updateProjectScreen(screenName, null)
    } else {
      error('You need at least one screen in your project')
    }
  }

  const findScreen = (screenName) => screens.find((s) => s.meta.name === screenName)

  const openScreen = (screenName) => {
    history.push(PATHS.PROJECT_DETAILS + project?.meta?.id + PATHS.SCREEN_DETAILS + screenName)
  }

  return {
    screens,
    addScreen,
    addProductScreen,
    moveScreen,
    updateScreen,
    updateScreenProperty,
    updateScreenProperties,
    setScreenContent,
    setScreen,
    setScreenWithoutEditor,
    renameScreen: renameProjectScreen,
    deleteScreen,
    findScreen,
    openScreen,
  }
}

function generateComponentsIds(content) {
  if (!Array.isArray(content)) {
    return content
  }

  return content.map(generateSingleComponentId)
}

function generateSingleComponentId(c) {
  if (!c) {
    return c
  }

  if (!Array.isArray(c)) {
    return {
      ...c,
      meta: {
        ...c.meta,
        id: c.meta?.id || shortid(),
      },
      content: generateComponentsIds(c.content),
    }
  }

  return c.map(generateSingleComponentId)
}
